import { ChangeEvent, CSSProperties, memo, useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { shallowEqual } from "react-redux"
import { AutoSizer } from "react-virtualized/dist/commonjs/AutoSizer"
import { areEqual, FixedSizeList } from "react-window"

import { useAppDispatch, useAppSelector } from "@/App"
import { Card } from "@/components/Card"
import { Panel } from "@/components/Panel"
import panelCls from "@/components/Panel/Panel.module.scss"
import { Status } from "@/components/Status"
import { OrderContextMenu, OrderContextMenuAction, OrderItemContext } from "@/modules/OrderContextMenu"
import { selectCount } from "@/store"
import { selectFilteredOrdersWithDriversIds, selectOrderById, selectOrdersStatus } from "@/store/orders"
import { setOrdersPanelSearchValue, setOrdersPanelSearchVisibility, setOrdersPanelTab } from "@/store/ui"
import { Order } from "@/types"
import { OrderUtils } from "@/utils"

import { OrderItem } from "./OrderItem"
import { OrderPanel } from "./OrderPanel"
import cls from "./Orders.module.scss"

const { filterByTab, filterBySearch, filterByStats, getTab } = OrderUtils

const TABS = ["unassigned", "assigned", "finished"] as const
const STATS_TABS = ["omitted"] as const

export type OrdersStatsTab = typeof STATS_TABS[number]
export type OrdersPanelTab = typeof TABS[number] | OrdersStatsTab | null

export const OrdersPanel = ({ className }: { className: string }) => {
  const dispatch = useAppDispatch()

  const ordersStatus = useAppSelector(selectOrdersStatus)
  const showOrderDetails = useAppSelector((state) => state.ui.showOrderDetails, shallowEqual)

  const currentTab = useAppSelector((state) => state.ui.ordersPanelTab)
  const setCurrentTab = useCallback((tabToSet: OrdersPanelTab) => dispatch(setOrdersPanelTab(tabToSet)), [dispatch])

  const searchValue = useAppSelector((state) => state.ui.ordersPanelSearchValue)
  const isSearchBoxShown = useAppSelector((state) => state.ui.isOrdersPanelSearchVisible)
  const setSearchValue = useCallback((value: string) => dispatch(setOrdersPanelSearchValue(value)), [dispatch])

  const filterOrderByCurrentTab = useCallback((order: Order) => getFilterByTab(order, currentTab), [currentTab])
  const filterOrderBySearch = useCallback((order: Order) => filterBySearch(order, searchValue), [searchValue])
  const filteredOrdersIds = useAppSelector(
    selectFilteredOrdersWithDriversIds(filterOrderByCurrentTab, filterOrderBySearch),
    shallowEqual
  )

  const [orderContextMenuAction, setOrderContextMenuAction] = useState<OrderContextMenuAction>(null)

  const [areStatsTabsExpanded, setAreStatsTabsExpanded] = useState(isStatsTab(currentTab) || isStatsTabSetFromParam())

  const listRef = useRef<FixedSizeList | null>(null)

  const handleTabChange = useCallback(
    (tabToSet: OrdersPanelTab) => {
      setCurrentTab(currentTab !== tabToSet ? tabToSet : null)
      listRef.current?.scrollToItem(0, "start")
    },
    [currentTab, setCurrentTab]
  )

  useScrollToOrderOnFocus({ filteredOrdersIds, listRef, currentTab, setCurrentTab })

  const { t } = useTranslation()

  function toggleSearchBox() {
    setSearchValue("")
    dispatch(setOrdersPanelSearchVisibility(!isSearchBoxShown))
  }

  useEffect(() => {
    return () => {
      setSearchValue("")
      dispatch(setOrdersPanelSearchVisibility(false))
    }
  }, [setSearchValue, dispatch])

  if (ordersStatus !== "fetched") {
    return (
      <Panel wrapperClassName={className}>
        <Status type={ordersStatus} title={t("orders")} fullHeight />
      </Panel>
    )
  }

  return (
    <OrderItemContext.Provider value={{ orderContextMenuAction, setOrderContextMenuAction }}>
      <Panel wrapperClassName={className}>
        <Panel.Header>
          <Panel.Title>
            {isSearchBoxShown ? (
              <Panel.SearchInput
                value={searchValue}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchValue(e.target.value)}
                placeholder={t("search_order")}
              />
            ) : (
              <Panel.Name>{t("orders")}</Panel.Name>
            )}
          </Panel.Title>

          <Panel.SearchButton
            isActive={isSearchBoxShown}
            title={isSearchBoxShown ? "Zamknij wyszukiwanie" : t("search_order")}
            onClick={toggleSearchBox}
          />

          <Panel.Tabs>
            {TABS.map((tab) => (
              <OrdersTab
                key={tab}
                tab={tab}
                currentTab={currentTab}
                searchValue={searchValue}
                handleTabChange={handleTabChange}
              />
            ))}

            <Panel.TabsDropdown active={areStatsTabsExpanded} setActive={setAreStatsTabsExpanded} />

            {areStatsTabsExpanded &&
              STATS_TABS.map((tab) => (
                <OrdersTab
                  key={tab}
                  tab={tab}
                  currentTab={currentTab}
                  searchValue={searchValue}
                  handleTabChange={handleTabChange}
                />
              ))}
          </Panel.Tabs>
        </Panel.Header>

        <Panel.Content>
          {filteredOrdersIds.length > 0 ? (
            <AutoSizer>
              {({ height, width }) => (
                <Card className={panelCls.card}>
                  <FixedSizeList
                    ref={listRef}
                    className={cls.ordersList}
                    height={height}
                    width={width}
                    itemData={filteredOrdersIds}
                    itemCount={filteredOrdersIds.length}
                    itemKey={(index, data) => data[index]}
                    itemSize={206}
                    overscanCount={2}
                  >
                    {OrdersRow}
                  </FixedSizeList>
                </Card>
              )}
            </AutoSizer>
          ) : (
            <div className={cls.empty}>{t("no_orders")}</div>
          )}
        </Panel.Content>

        {showOrderDetails && <OrderPanel id={showOrderDetails.id} type={showOrderDetails.type} />}

        <OrderContextMenu />
      </Panel>
    </OrderItemContext.Provider>
  )
}

type OrdersRowProps = {
  data: Array<Order["id"]>
  index: number
  style: CSSProperties
}

const OrdersRow = memo(({ data, index, style }: OrdersRowProps) => {
  const orderId = data[index]
  return <OrderItem key={orderId} orderId={orderId} style={{ ...style, padding: "1.5rem" }} />
}, areEqual)

type OrdersTabProps = {
  tab: OrdersPanelTab
  currentTab: OrdersPanelTab
  searchValue: string
  handleTabChange: (tabToSet: OrdersPanelTab) => void
}

const OrdersTab = memo(({ tab, currentTab, searchValue, handleTabChange }: OrdersTabProps) => {
  const filterOrderByTab = useCallback((order: Order) => getFilterByTab(order, tab), [tab])
  const filterOrderBySearch = useCallback((order: Order) => filterBySearch(order, searchValue), [searchValue])
  const ordersForTabCount = useAppSelector(
    selectCount(selectFilteredOrdersWithDriversIds(filterOrderByTab, filterOrderBySearch))
  )

  const { t } = useTranslation()

  return (
    <Panel.Tab
      label={t(`orders:${tab}`)}
      value={ordersForTabCount}
      active={currentTab === tab}
      onClick={() => handleTabChange(tab)}
    />
  )
})

type useScrollToOrderOnFocusOptions = {
  currentTab: OrdersPanelTab
  filteredOrdersIds: Array<Order["id"]>
  listRef: React.MutableRefObject<FixedSizeList | null>
  setCurrentTab: (tabToSet: OrdersPanelTab) => void
}

function useScrollToOrderOnFocus({
  currentTab,
  setCurrentTab,
  filteredOrdersIds,
  listRef,
}: useScrollToOrderOnFocusOptions) {
  const focusedOrderId = useAppSelector((state) => state.ui.focusedOrderId)
  const focusedOrder = useAppSelector(selectOrderById(focusedOrderId!))
  const focusedOrderIndex = filteredOrdersIds.indexOf(focusedOrderId!)

  useEffect(() => {
    if (!focusedOrder) return

    if (currentTab !== getTab(focusedOrder)) {
      setCurrentTab(getTab(focusedOrder) || null)
    }

    // eslint-disable-next-line
  }, [focusedOrderId])

  useEffect(() => {
    if (!focusedOrder) return

    // setTimeout(() => {
    listRef.current?.scrollToItem(focusedOrderIndex)
    // }, 0) // schedules execution after current call stack has executed

    // eslint-disable-next-line
  }, [focusedOrderId, focusedOrderIndex])
}

function isStatsTab(checkedTab: string | null): checkedTab is OrdersStatsTab {
  return STATS_TABS.some((tab) => tab === checkedTab)
}

function isStatsTabSetFromParam() {
  return (
    window.location.search.includes("orders_tab") &&
    isStatsTab(new URLSearchParams(window.location.search).get("orders_tab"))
  )
}

function getFilterByTab(order: Order, tab: OrdersPanelTab): boolean {
  return isStatsTab(tab) ? filterByStats(order, tab) : filterByTab(order, tab)
}
